"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { trackUserSignUp } from "@/lib/analytics-service"
import { appleProvider, auth, googleProvider } from "@/lib/firebase-client"
import { ensureUserProfileDocument } from "@/lib/profile-service"
import { zodResolver } from "@hookform/resolvers/zod"
import { FirebaseError } from "firebase/app"
import { 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  updateProfile,
} from "firebase/auth"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  phone: z.string().min(7, {
    message: "Please enter a valid phone number.",
  }).regex(/^[+\d][\d\s\-().]+$/, {
    message: "Please enter a valid phone number.",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters.",
  }),
})

type SignupFormProps = {
  redirectTo?: string;
};

export function SignupForm({ redirectTo }: SignupFormProps) {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false)
  const [oauthPhone, setOauthPhone] = useState("")
  const [oauthPhoneError, setOauthPhoneError] = useState<string | null>(null)
  const [pendingOAuthUser, setPendingOAuthUser] = useState<import("firebase/auth").User | null>(null)
  const [pendingOAuthProvider, setPendingOAuthProvider] = useState<string | null>(null)
  const [pendingOAuthOverrides, setPendingOAuthOverrides] = useState<Record<string, string | undefined>>({})
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, values.email, values.password)

      const trimmedName = values.name.trim()
      if (trimmedName) {
        await updateProfile(userCredential.user, { displayName: trimmedName })
      }

      try {
        await ensureUserProfileDocument(userCredential.user, {
          displayName: trimmedName || undefined,
          phoneNumber: values.phone.trim() || undefined,
        })
      } catch (profileError) {
        console.error("Failed to sync profile after signup", profileError)
      }
      trackUserSignUp("email")
      navigate(redirectTo || "/home")
    } catch (err) {
      const message = err instanceof FirebaseError ? mapSignupError(err.code) : "Could not create account. Please try again."
      setErrorMessage(message)
      form.setError("email", { message })
      form.setError("password", { message: " " })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleGoogleSignUp() {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const credential = await signInWithPopup(auth, googleProvider)
      setPendingOAuthUser(credential.user)
      setPendingOAuthProvider("google")
      setPendingOAuthOverrides({
        displayName: credential.user.displayName ?? undefined,
        avatarURL: credential.user.photoURL ?? undefined,
      })
      setPhoneDialogOpen(true)
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        // Handle case where account already exists
        if (err.code === "auth/account-exists-with-different-credential") {
          const email = err.customData?.email as string | undefined;
          if (email) {
            setErrorMessage(
              "An account with this email already exists. Please sign in instead."
            );
          } else {
            setErrorMessage(
              "An account already exists. Please sign in instead."
            );
          }
        } else {
          const message = mapSignupError(err.code);
          setErrorMessage(message);
        }
      } else {
        setErrorMessage("Google sign-up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleAppleSignUp() {
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      const credential = await signInWithPopup(auth, appleProvider)
      // Apple may not provide displayName on first sign-in, it's in the credential
      type CredentialWithAdditionalInfo = typeof credential & {
        additionalUserInfo?: {
          profile?: {
            name?: {
              firstName?: string;
              lastName?: string;
            };
          };
        };
      };
      const credentialWithInfo = credential as CredentialWithAdditionalInfo;
      const displayName = credential.user.displayName ??
        (credentialWithInfo.additionalUserInfo?.profile?.name
          ? `${credentialWithInfo.additionalUserInfo.profile.name.firstName ?? ""} ${credentialWithInfo.additionalUserInfo.profile.name.lastName ?? ""}`.trim()
          : undefined);

      setPendingOAuthUser(credential.user)
      setPendingOAuthProvider("apple")
      setPendingOAuthOverrides({
        displayName,
        avatarURL: credential.user.photoURL ?? undefined,
      })
      setPhoneDialogOpen(true)
    } catch (err) {
      console.error(err);
      if (err instanceof FirebaseError) {
        // Handle case where account already exists
        if (err.code === "auth/account-exists-with-different-credential") {
          const email = err.customData?.email as string | undefined;
          if (email) {
            setErrorMessage(
              "An account with this email already exists. Please sign in instead."
            );
          } else {
            setErrorMessage(
              "An account already exists. Please sign in instead."
            );
          }
        } else {
          const message = mapSignupError(err.code);
          setErrorMessage(message);
        }
      } else {
        setErrorMessage("Apple sign-up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleOAuthPhoneSubmit() {
    if (!pendingOAuthUser) return

    const trimmedPhone = oauthPhone.trim()
    if (!trimmedPhone || trimmedPhone.length < 7) {
      setOauthPhoneError("Please enter a valid phone number.")
      return
    }

    setOauthPhoneError(null)
    setIsSubmitting(true)

    try {
      await ensureUserProfileDocument(pendingOAuthUser, {
        ...pendingOAuthOverrides,
        phoneNumber: trimmedPhone,
      })
      trackUserSignUp((pendingOAuthProvider as "email" | "google" | "apple") || "email")
      setPhoneDialogOpen(false)
      navigate(redirectTo || "/home")
    } catch (profileError) {
      console.error("Failed to save phone number after OAuth signup", profileError)
      // Still navigate — the profile was likely created, just phone wasn't saved
      setPhoneDialogOpen(false)
      navigate(redirectTo || "/home")
    } finally {
      setIsSubmitting(false)
      setPendingOAuthUser(null)
      setPendingOAuthProvider(null)
      setPendingOAuthOverrides({})
    }
  }

  return (
    <>
    <Dialog open={phoneDialogOpen} onOpenChange={(open) => {
      if (!open && pendingOAuthUser) {
        // User dismissed — save profile without phone and navigate
        ensureUserProfileDocument(pendingOAuthUser, pendingOAuthOverrides).catch(() => {})
        trackUserSignUp((pendingOAuthProvider as "email" | "google" | "apple") || "email")
        setPendingOAuthUser(null)
        setPendingOAuthProvider(null)
        setPendingOAuthOverrides({})
        setPhoneDialogOpen(false)
        navigate(redirectTo || "/home")
      }
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Your Phone Number</DialogTitle>
          <DialogDescription>
            Please provide your phone number so we can match invitations sent to you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="oauth-phone">Phone Number</Label>
          <Input
            id="oauth-phone"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={oauthPhone}
            onChange={(e) => setOauthPhone(e.target.value)}
          />
          {oauthPhoneError ? (
            <p className="text-sm text-destructive">{oauthPhoneError}</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button onClick={handleOAuthPhoneSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Card>
      <CardContent className="p-6">
        <div className="space-y-2">
          <Button type="button" variant="outline" className="w-full" onClick={handleGoogleSignUp} disabled={isSubmitting}>
            <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 23.4 172.9 61.9l-76.2 76.2c-27.3-26.2-62.7-42.5-100.9-42.5-87 0-157.9 71-157.9 158s70.9 158 157.9 158c99.9 0 137.8-70.2 142.4-107.4H248v-91.1h236.3c4.7 25.4 7.7 54.2 7.7 85.9z"></path></svg>
            Sign up with Google
          </Button>
          <Button type="button" variant="outline" className="w-full bg-black text-white hover:bg-gray-800" onClick={handleAppleSignUp} disabled={isSubmitting}>
            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Sign up with Apple
          </Button>
        </div>
        <div className="relative my-4">
          <Separator />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 bg-card">
            <span className="text-sm text-muted-foreground">OR CONTINUE WITH</span>
          </div>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="name@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {errorMessage ? (
              <p className="text-sm text-destructive" role="alert">{errorMessage}</p>
            ) : null}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Creating account..." : "Create Account"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
    </>
  )
}

function mapSignupError(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "An account with this email already exists."
    case "auth/invalid-email":
      return "Please enter a valid email address."
    case "auth/weak-password":
      return "Password must be at least 6 characters."
    case "auth/popup-closed-by-user":
      return "Sign-up was cancelled before completion."
    case "auth/account-exists-with-different-credential":
      return "An account with this email already exists. Please sign in instead."
    case "auth/popup-blocked":
      return "Pop-up was blocked by your browser. Please allow pop-ups for this site."
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again."
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later."
    case "auth/operation-not-allowed":
      return "Google sign-in is not enabled. Please contact support."
    default:
      return "Authentication failed. Please try again."
  }
}
